import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import CostSummaryCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostSummaryCard';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatTokens from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatTokens';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import resolveDeviationChip from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/resolveDeviationChip';
import resolveDrillAction from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/resolveDrillAction';
import subjectDimensionLabels, {
	type SubjectDimension,
} from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectDimensionLabels';
import LimitCell from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/LimitCell';
import type AiCostLimitSubjectsView from '@UI/Store/AiCostLimits/Views/AiCostLimitSubjectsView';
import type AiCostLimitsListView from '@UI/Store/AiCostLimits/Views/AiCostLimitsListView';
import formatEuros from '@UI/Helpers/formatEuros';
import formatAudioDuration from '@UI/Pages/Administration/AiCosts/Helpers/formatAudioDuration';
import type AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import { css } from '@emotion/react';
import * as React from 'react';

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props = {
	readonly list: AiCostBreakdownListView;
	readonly dimension: SubjectDimension;
	readonly total: AiCostSummaryView | null;
	readonly reference: AiCostSummaryView | null;
	/** Both `null` when the limit column is not offered. */
	readonly limits: AiCostLimitsListView | null;
	readonly subjects: AiCostLimitSubjectsView | null;
	readonly showsConsumption: boolean;
};

const describeAudio = (audio: AiCostBreakdownRowView['audio']): string =>
	`Audio : ${formatQuantity(audio.count, 'audio', 'audios')} · ${formatAudioDuration(audio.durationSeconds)} · ${formatEuros(audio.costAmount)}`;

const describeDocuments = (documents: AiCostBreakdownRowView['documents']): string =>
	`Documents : ${formatQuantity(documents.count, 'document', 'documents')} · ${formatEuros(documents.costAmount)}`;

const describeTechnicalData = (tokens: number | null, callCount: number | null): string | null => {
	if (tokens === null || callCount === null) {
		return null;
	}

	return `Jetons ${formatTokens(tokens)} · ${formatQuantity(callCount, 'appel', 'appels')}`;
};

/** The rows of a subject table as cards, on a narrow screen, closed by the dark Total card. */
const SubjectCostCards: React.FC<Props> = ({
	list,
	dimension,
	total,
	reference,
	limits,
	subjects,
	showsConsumption,
}) => {
	const labels = subjectDimensionLabels[dimension];

	const resolveChip = React.useCallback(
		(row: AiCostBreakdownRowView): string | null => {
			if (row.unpricedItemCount > 0) {
				return `dont ${formatQuantity(row.unpricedItemCount, 'traitement sans tarif', 'traitements sans tarif')}`;
			}

			const documentsDeviation = resolveDeviationChip(
				row.documents.averageCost,
				reference?.documents.averageCost ?? null,
				row.documents.count,
			);

			if (documentsDeviation !== null) {
				return `Documents : ${documentsDeviation}`;
			}

			const audioDeviation = resolveDeviationChip(
				row.audio.averageCost,
				reference?.audio.averageCost ?? null,
				row.audio.count,
			);

			return audioDeviation === null ? null : `Audios : ${audioDeviation}`;
		},
		[reference],
	);

	return (
		<div css={listStyle}>
			{list.list.map((row) => {
				const subject = resolveSubjectLabel(dimension, row);
				const technicalData = describeTechnicalData(
					row.quantities?.totalTokens ?? null,
					row.callCount,
				);
				const lines = [
					subject.detail,
					describeAudio(row.audio),
					describeDocuments(row.documents),
					technicalData,
				];

				return (
					<CostSummaryCard
						key={row.subjectId ?? 'unattributed'}
						title={subject.title}
						amount={formatEuros(row.costAmount)}
						lines={lines.filter((line): line is string => line !== null)}
						chip={resolveChip(row)}
						chipTone={row.unpricedItemCount > 0 ? 'warning' : 'danger'}
						isMuted={subject.isMuted}
						drill={resolveDrillAction(dimension, row, subject)}
						footer={
							limits === null || subjects === null ? null : (
								<LimitCell
									row={row}
									dimension={dimension}
									limits={limits}
									subjects={subjects}
									showsConsumption={showsConsumption}
								/>
							)
						}
					/>
				);
			})}
			<ConditionalLoader data={total}>
				{(summary) => (
					<CostSummaryCard
						isInverted
						title="Total"
						amount={formatEuros(summary.costAmount)}
						lines={[
							formatQuantity(list.totalRecords, labels.singular, labels.plural),
							describeAudio(summary.audio),
							describeDocuments(summary.documents),
						]}
						chip={
							summary.unpricedItemCount > 0
								? `dont ${formatQuantity(summary.unpricedItemCount, 'traitement sans tarif', 'traitements sans tarif')}`
								: null
						}
					/>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default SubjectCostCards;
